import {
  ACCEPTED_ARTICLE_TYPES,
  CompanyType,
  FALLBACK_PMT_LANGUAGE_CODE,
  MULTI_LANGUAGE_PAGES_REGEX,
  ROUTES,
  RouteName,
} from '@/constants';
import { MULTI_LANGUAGE_PAGE_SLUGS } from '@/constants/routes.constants';
import { LanguageCode } from '@/interfaces';
import getConfig from 'next/config';
import replacePath from './replacePath.utils';

export const getPathnameFromURL = (url: string): string => {
  try {
    const parsedURL = new URL(url);

    return parsedURL.pathname;
  } catch {
    return '';
  }
};

// If true, open in new tab
export const isExternalURL = (url: string | undefined): boolean => {
  if (!url) return false;
  const { publicRuntimeConfig } = getConfig();
  const { origin } = publicRuntimeConfig;
  const parsedURL = isAbsoluteUrl(url) && new URL(url);
  if (!parsedURL) return false;

  const parsedOrigin = `${parsedURL.protocol}//${parsedURL.host}`;
  return parsedOrigin !== origin;
};

export const isAbsoluteUrl = (url?: string): boolean => !!url && /^[a-z][a-z0-9+.-]*:/.test(url);

export const getArticleTypeNameFromSlug = (articleTypeSlug: string): string | null => {
  let articleTypeName: string | null = null;

  Object.entries(ACCEPTED_ARTICLE_TYPES).some(([name, slug]) => {
    if (slug === articleTypeSlug) {
      articleTypeName = name;

      return true;
    }
  });

  return articleTypeName;
};

export const getAcceptedCompanyType = (companyType: string): string | null => {
  const isAccepted = Object.values<string>(CompanyType).includes(companyType);
  return isAccepted ? companyType : null;
};

export const isOpenInNewTab = (url: string) => {
  if (isExternalURL(url)) return '_blank';
  return undefined;
};

type ModifyUrlQuery = (url: string, modifier: (previousQuery: unknown) => Record<string, string | number>) => string;

export const modifyUrlQuery: ModifyUrlQuery = (url, modifier) => {
  const hasQuery = url.includes('?');
  const previousQuery: Record<string, string | number> = {};

  if (hasQuery) {
    const queryString = url.split('?').pop() as string;

    queryString.split('&').forEach((item) => {
      const [key, value] = item.split('=');

      previousQuery[key] = value;
    });
  }

  const newQuery = modifier(previousQuery as any);
  const originalPath = url.split('?').shift();
  const newQueryString = Object.entries(newQuery)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return `${originalPath}?${newQueryString}`;
};

export const generateSquareLogo = (logo: string) =>
  logo &&
  `https://assets.parcelperform.com/pp-web-app-assets/logo-carriers/square-logo-carries/${logo.split(/[/]+/).pop()}`;

// For PMT
export const checkTrackMyParcelPage = (path: string) => {
  const trackMyParcelMatch = path.match(MULTI_LANGUAGE_PAGES_REGEX);
  let trackMyParcelLang = FALLBACK_PMT_LANGUAGE_CODE;
  if (trackMyParcelMatch) {
    const isTrackMyParcelLang = Object.values(LanguageCode).includes(trackMyParcelMatch[2] as LanguageCode);
    if (isTrackMyParcelLang) trackMyParcelLang = trackMyParcelMatch[2] as LanguageCode;
  }

  return {
    isTrackMyParcelPage: !!trackMyParcelMatch,
    languageCode: trackMyParcelLang,
  };
};

type GetPmtLangHref = (
  languageCode?: LanguageCode,
  routeName?: {
    defaultLang: RouteName;
    multiLang: RouteName;
  },
) => string;

// For PMT
export const getPmtLangHref: GetPmtLangHref = (languageCode, routeName) => {
  if (!languageCode) return '#';
  if (languageCode === LanguageCode.English) return ROUTES[routeName?.defaultLang || RouteName.TrackMyParcelEn].path;
  return replacePath(routeName?.multiLang || RouteName.TrackMyParcelMultiLanguages, [languageCode]);
};

/**
 * Get full url from anchor tag's href
 * @param href string
 * @returns string
 */
export const getFullUrlProps = (link: string) => {
  const { publicRuntimeConfig } = getConfig();
  const { origin } = publicRuntimeConfig;
  // check if linkUrl is text with no http or https at start, and no slash at start
  // e.g. localhost:3000, insights, insights/fr
  const isText = !link.match(/^(http|https):\/\//) && !link.match(/^\//);
  const href = isText ? `/${link}` : link;

  const isAbsoluteUrl = /^https?:\/\//.test(href);
  const fullUrl = isAbsoluteUrl ? href : `${origin}${href}`;

  const { host, protocol, pathname } = new URL(fullUrl);

  return {
    pathname,
    url: fullUrl,
    origin: `${protocol}//${host}`,
  };
};

export const isTrackMyParcelLink = (link: string) => {
  const { pathname } = getFullUrlProps(link);
  const isTrackMyParcelMatch = pathname.match(MULTI_LANGUAGE_PAGES_REGEX);
  return !!isTrackMyParcelMatch;
};

export const isInternalPmcLink = (link: string) => {
  const { publicRuntimeConfig } = getConfig();
  const { origin } = publicRuntimeConfig;
  const { origin: linkOrigin } = getFullUrlProps(link);

  // for cases of https://google.com/insights <= !isTrackMyParcel = true, isHostnameMatch = false
  const isHostnameMatch = linkOrigin === origin;
  return !isTrackMyParcelLink(link) && isHostnameMatch;
};

// Pages that can have LeadsWrapper: Individual Carrier, Company Directory, Contact Us
export const checkIsLeadsWrapperPages = (url: string) => {
  const pathname = getPathnameFromURL(url);
  const isLeadsWrapperPages = pathname.match(/^\/(companies\/carriers(\/[\d\w-_]*)?|contact-us)$/);
  return !!isLeadsWrapperPages;
};

export const getFileNameFromFileUrl = (fileUrl?: string) => fileUrl?.split('/').pop() ?? '';

export const isValidLanguageCode = (languageCode: string) =>
  languageCode && Object.values(LanguageCode).includes(languageCode as LanguageCode);

/**
 * Checks if a pathname matches a specific page slug, considering language code prefixes.
 *
 * This function validates two scenarios:
 * 1. Direct match: when pathname exactly matches the pageSlug (e.g., "insights")
 * 2. Language prefixed match: when pathname has a valid language code prefix (e.g., "fr/insights")
 *
 * @param pathname - The URL pathname to check (e.g., "insights" or "fr/insights")
 * @param pageSlug - The page slug to match against (e.g., "insights")
 *
 * @example
 * // Direct match
 * checkPageWithLanguageCode("insights", "insights") // returns true
 * checkPageWithLanguageCode("about", "about") // returns true
 *
 * // Language prefixed match
 * checkPageWithLanguageCode("fr/insights", "insights") // returns true
 * checkPageWithLanguageCode("de/insights", "insights") // returns true
 *
 * // Non-matches
 * checkPageWithLanguageCode("fr/about", "insights") // returns false
 * checkPageWithLanguageCode("invalid/insights", "insights") // returns false (invalid language code)
 *
 * @returns {boolean} True if the pathname matches the pageSlug pattern, false otherwise
 */
export const checkPageWithLanguageCode = (pathname: string, pageSlug: string) => {
  const splitPathname = pathname.split('/').slice(1);

  if (splitPathname[0] === pageSlug) return true;

  const [languageCode, slug] = splitPathname;

  const validLanguageCode = isValidLanguageCode(languageCode);
  if (validLanguageCode && pageSlug === slug) return true;

  return false;
};

/**
 * Checks if a pathname corresponds to a multi-language page by comparing it against predefined slugs.
 *
 * This function validates if the given pathname matches any of the multi-language page slugs defined in
 * MULTI_LANGUAGE_PAGE_SLUGS, taking into account potential language code prefixes.
 *
 * @param pathname - The URL pathname to check (e.g., "track-my-parcel" or "fr/track-my-parcel")
 *
 * @example
 * // Direct match
 * checkIsMultiLanguagePage("track-my-parcel") // returns true
 * checkIsMultiLanguagePage("account") // returns true
 *
 * // Language prefixed match
 * checkIsMultiLanguagePage("fr/track-my-parcel") // returns true
 * checkIsMultiLanguagePage("de/account") // returns true
 *
 * // Non-matches
 * checkIsMultiLanguagePage("about") // returns false
 * checkIsMultiLanguagePage("fr/about") // returns false
 *
 * @returns {boolean} True if the pathname corresponds to a multi-language page, false otherwise
 */
export const checkIsMultiLanguagePage = (pathname: string) => {
  return MULTI_LANGUAGE_PAGE_SLUGS.some((slug) => checkPageWithLanguageCode(pathname, slug));
};

export const getCurrentLanguageCodeFromPathname = (pathname: string): LanguageCode => {
  const splitPathname = pathname.split('/').slice(1);
  const [languageCode] = splitPathname;

  return isValidLanguageCode(languageCode) ? (languageCode as LanguageCode) : FALLBACK_PMT_LANGUAGE_CODE;
};

/**
 * Extracts UTM parameters from the URL query string
 *
 * @returns {Record<string, string>} Object containing all UTM parameters
 *
 * Example:
 * For URL: /page?utm_source=google&utm_medium=cpc&other=value
 * Returns: { utm_source: 'google', utm_medium: 'cpc' }
 *
 * - Filters query params to only get those starting with 'utm_'
 * - Builds an object with the UTM params and their values
 * - Ignores any non-UTM query parameters
 */
export const getUtmParams = (query: Record<string, string | string[] | undefined>) => {
  const utmParams = Object.keys(query)
    .filter((key) => key.startsWith('utm_'))
    .reduce(
      (acc, key) => {
        acc[key] = query[key] as string;
        return acc;
      },
      {} as Record<string, string>,
    );

  return utmParams;
};
